
import { Injectable } from '@angular/core';
import { I18nService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { IndicatorService } from 'app/common/services';
import { FeatureConfig } from 'app/config/feature';

@Injectable({ providedIn: 'root' })
export class AnalysisStrService {
    /** 二级储存屏蔽对象 */
    oceanProtectObjList = ['SMARTCACHEPOOL', 'SmartMigration', 'ISCSI_LINK', 'eFC_LINK', 'eISCSI_LINK'];
    /** dataTurbo相关对象 */
    dataTurboObjArr = ['DATATURBOCONTROLLER', 'DATATURBOHOST'];
    /** 仅二级存储支持的指标ID数组 */
    oceanProtectIndicator = [1263, 1264, 1265, 1266, 1267, 1268, 1269, 1270, 1271, 1272, 1273, 1274, 1275, 1276];
    /**
     * KV特性对象数组
     */
    kvObjArr = ['DATATURBOHOST', 'ETH_PORT', 'LIF_PORT', 'DATATURBOCONTROLLER', 'BOND_PORT'];

    // 微存储屏蔽对象
    // 微存储混闪要支持SAS端口
    oceanMicroObjList = ['SNAPSHOT', ...(FeatureConfig.IsSupportSASPort ? [] : ['SAS_PORT']), 'OM_OBJ_REMOTE_DEVICE', 'FC_LINK',
        'OM_OBJ_IP_LINK', 'ISCSI_LINK', 'eISCSI_LINK', 'eFC_LINK', 'REPLICATIONPAIR', 'CONSISTENTGROUP',
        'SmartMigration', 'SMARTCACHEPOOL'];

    // 微存储直通屏蔽对象
    oceanMicroDirectObjList = ['STORAGEPOOL', 'SmartQoS', 'LUNPRIORITYSTATISTIC', 'FC_PORT', 'ETH_PORT'];

    // 使用addLinkComponent组件的对象
    ANALYSIS_LINK_OBJ_LIST = [
        // 远端设备
        'OM_OBJ_REMOTE_DEVICE',
        // 复制FC链路
        'FC_LINK',
        // IP复制链路
        'OM_OBJ_IP_LINK',
        // iSCSI远端链路
        'ISCSI_LINK',
        // 异构FC链路
        'eFC_LINK',
        // 异构ISCSI链路
        'eISCSI_LINK',
        // 硬盘
        'DISK',
    ];

    get = this.i18nService.get;

    i18n = {
        totalLab: this.get('common.total_lab'),
        createBtn: this.get('common.create_button'),
        modifyBtn: this.get('common.modify_button'),
        deleteBtn: this.get('common.delete_button'),
        copyBtn: this.get('common.copy_button'),
        refreshBtn: this.get('system.hardware_term_refresh_button'),
        infoTitle: this.get('common.EnumDefine_DANGER_LEVEL_E_INFOR'),
        confirmTitle: this.get('common.confirm_lab'),
        configuredPmsLunTip: this.get('monitor.charts_term_noConfigPMS_tip1'),
        ensureToUseAnalysisTip: this.get('monitor.charts_term_noConfigPMS_tip2'),
        switchStandardTip: this.i18nService.get('home.performance_term_enable_switchStandard_tip'),
        goToConfiguredPmsLunPageTip: this.get('monitor.charts_term_noConfigPMS_tip3'),
        period: this.get('common.Punctuation_preiod'),
        createTitle: this.get('monitor.charts_term_createChart_title'),
        selectChartTip: this.get('monitor.charts_term_chooseChart_desc_label'),
        noChartTip: this.get('monitor.charts_term_createChart_desc_label'),
        loading: this.get('common.loading_label'),
        excessLunTip: this.get('monitor.monitor_term_customQueryFail_des_lab'),
        pmsStatusAbnormalTip: this.get('monitor.charts_term_repairPerforMonitorData_tip'),
        createNowbtn: this.get('service.Cloudtier_task_noData_info2'),
        objectType: this.i18nService.get('monitor.charts_term_objectType_label'),
        objects: this.i18nService.get('monitor.charts_term_objectInstance_label'),
        indicator: this.i18nService.get('monitor.charts_term_Indicator_label'),
        value: this.i18nService.get('monitor.charts_term_value_col'),
        time: this.i18nService.get('common.time_label'),
        objectTypeId: this.i18nService.get('monitor.charts_term_objectTypeID_label'),
        objectsId: this.i18nService.get('monitor.charts_term_objectInstanceID_label'),
        indicatorId: this.i18nService.get('monitor.charts_term_IndicatorID_label'),
        timeRangeTip: this.get('common.timeRange_tip'),

        // 检测阈值
        thresholdLab: (threshold?: number, unit?: string): string =>
            this.get('monitor.anomalyDetect_task_detectionThreshold_lab', [threshold, unit]),

        // 退出保存临时图表提示
        saveTempChartTipsTitle: this.get('monitor.charts_term_saveChart_title'),
        saveTempChartTipsContent: this.get('monitor.charts_term_saveChart_confirm_info'),
    };

    sideOpts: SelectOption[] = [
        {
            value: 'left',
            label: 'Left',
        },
        {
            value: 'right',
            label: 'Right',
        },
    ];

    /**
     * 指标问号提示信息
     */
    indicatorHelpContent = {
        1626: this.i18nService.get('monitor.performance_term_OPSSmartMove_tip'),
        1627: this.i18nService.get('monitor.performance_term_readOPSSmartMove_tip'),
        1628: this.i18nService.get('monitor.performance_term_writeOPSSmartMove_tip'),
        1629: this.i18nService.get('monitor.performance_term_readBandwidthSmartMove_tip'),
        1630: this.i18nService.get('monitor.performance_term_writeBandwidthSmartMove_tip'),
    };

    /**
     * 性能图表类型时间段选项
     */
    performanceChartTimeOption = [
        {
            value: 'five_minutes',
            label: this.i18nService.get('common.lastestFiveMinute_listbox'),
        },
        {
            value: 'half_hour',
            label: this.i18nService.get('common.lastestHalfanHour_listbox'),
        },
        {
            value: 'one_day',
            label: this.i18nService.get('common.lastestDay_listbox'),
        },
        {
            value: 'one_week',
            label: this.i18nService.get('common.lastestWeek_listbox'),
        },
        {
            value: 'one_month',
            label: this.i18nService.get('common.lastestMonth_listbox'),
        },
        {
            value: 'one_year',
            label: this.i18nService.get('common.lastestYear_listbox'),
        },
        {
            value: 'customer',
            label: this.i18nService.get('common.custom_value'),
        },
    ];

    /**
     * 容量图表类型时间段选项
     */
    capacityChartTimeOption = [
        {
            value: 'one_day',
            label: this.i18nService.get('common.lastestDay_listbox'),
        },
        {
            value: 'one_week',
            label: this.i18nService.get('common.lastestWeek_listbox'),
        },
        {
            value: 'one_month',
            label: this.i18nService.get('common.lastestMonth_listbox'),
        },
        {
            value: 'one_year',
            label: this.i18nService.get('common.lastestYear_listbox'),
        },
        {
            value: 'customer',
            label: this.i18nService.get('common.custom_value'),
        },
    ];

    /**
     * 统计对象排序选项
     */
    monitoredObjSortOption = [
        {
            value: {
                sortType: 'customer',
                limit: 0,
            },
            label: this.i18nService.get('common.custom_value'),
        },
        {
            value: {
                sortType: 'top',
                limit: 5,
            },
            label: this.i18nService.get('monitor.charts_term_top5_label'),
        },
        {
            value: {
                sortType: 'top',
                limit: 10,
            },
            label: this.i18nService.get('monitor.charts_term_top10_label'),
        },
        {
            value: {
                sortType: 'top',
                limit: 16,
            },
            label: this.i18nService.get('monitor.charts_term_top16_label'),
        },
        {
            value: {
                sortType: 'bottom',
                limit: 5,
            },
            label: this.i18nService.get('monitor.charts_term_bottom5_label'),
        },
        {
            value: {
                sortType: 'bottom',
                limit: 10,
            },
            label: this.i18nService.get('monitor.charts_term_bottom10_label'),
        },
        {
            value: {
                sortType: 'bottom',
                limit: 16,
            },
            label: this.i18nService.get('monitor.charts_term_bottom16_label'),
        },
    ];

    // 阵列内SmartMove&阵列间SmartMove需要问号提示的指标
    smartMoveHelpContentIndicatorList = [1626, 1627, 1628, 1629, 1630];

    constructor(
        private i18nService: I18nService,
        private indicatorService: IndicatorService,
    ) { }

    getIndicatorNameAndUnit(id: number): { name: string; unit: string } {
        return this.indicatorService.getIndicatorNameAndUnit(id);
    }

    /**
     * 最大图表数提示
     * @param num
     */
    getChartUpperMsg(num: number): string {
        return this.get('monitor.charts_term_createChartUpper_msg', [num]);
    }

    /**
     * 对象被删除的提示信息
     */
    objDeletedRender = (id: string): string => this.get('monitor.charts_term_chart_deleted_tip', [id]);

    /**
     * 对象失效提示信息
     */
    objInvalidRender = (id: string): string => this.get('monitor.charts_term_chart_invalid_tip', [id]);
}
